import { useNavigate } from 'react-router-dom'
import { User } from '../types'
import { hasFinancistAccess, hasPurchaseReferentAccess } from '../lib/roleFinancialAccess'
import { isSchoolEmployeeUser } from '../lib/userListFilters'
import './RoleSelector.css'

export type PurchaseUiRole = 'director' | 'financist' | 'referent' | 'vedouci' | 'zadatel'

interface PurchaseRoleSelectorProps {
  onRoleSelect: (role: PurchaseUiRole) => void
  currentUser?: User
}

/** Stejný pool jako backend purchase_user_can_act_as_vedouci */
export function canActAsPurchaseLeader(user?: User | null): boolean {
  if (!user) return false
  const isITAdmin = user.role === 'admin' && user.admin_type === 'spravce_it'
  const isBuildingAdmin = user.role === 'admin' && user.admin_type === 'spravce_budov'
  if (isITAdmin || isBuildingAdmin) return true
  if (
    user.role === 'vedouci' ||
    user.role === 'vedouci_executor' ||
    user.role === 'zastupce_reditele' ||
    user.role === 'zocmos' ||
    user.role === 'director' ||
    user.role === 'property_referent' ||
    user.role === 'spravce_rozvrhu' ||
    user.role === 'spravce_jidelny'
  ) {
    return true
  }
  return hasFinancistAccess(user)
}

function PurchaseRoleSelector({ onRoleSelect, currentUser }: PurchaseRoleSelectorProps) {
  const navigate = useNavigate()

  const getRoleLabel = (role: string | null) => {
    if (!role) return 'Bez role'
    switch (role) {
      case 'director':
        return 'Ředitel'
      case 'financist':
        return 'Hlavní ekonom'
      case 'referent':
        return 'Obchodní referent'
      case 'referent_eko':
        return 'Referent EKO'
      case 'vedouci':
        return 'Vedoucí'
      case 'zadatel':
        return 'Žadatel'
      default:
        return role
    }
  }

  const isITAdmin = currentUser?.role === 'admin' && currentUser?.admin_type === 'spravce_it'
  const canBeDirector = currentUser?.role === 'director' || isITAdmin
  const canBeFinancist = hasFinancistAccess(currentUser)
  const canBeReferent = hasPurchaseReferentAccess(currentUser)
  const canBeVedouci = canActAsPurchaseLeader(currentUser)
  const canBeZadatel =
    isITAdmin || (currentUser ? isSchoolEmployeeUser(currentUser) : false)

  const handleRoleClick = (role: PurchaseUiRole) => {
    if (isITAdmin) {
      onRoleSelect(role)
      return
    }

    let hasAccess = false
    switch (role) {
      case 'director':
        hasAccess = canBeDirector
        break
      case 'financist':
        hasAccess = canBeFinancist
        break
      case 'referent':
        hasAccess = canBeReferent
        break
      case 'vedouci':
        hasAccess = canBeVedouci
        break
      case 'zadatel':
        hasAccess = canBeZadatel
        break
    }

    if (!hasAccess) {
      alert('Nemáte oprávnění k přístupu jako ' + getRoleLabel(role) + '. Kontaktujte IT administrátora.')
      return
    }

    onRoleSelect(role)
  }

  return (
    <div className="role-selector">
      <div className="role-selector-container">
        <button
          className="back-to-roles-button"
          onClick={() => navigate('/intranet')}
          style={{ marginBottom: '1rem' }}
        >
          ← Zpět
        </button>
        <h1>Žádanky o nákup</h1>
        <p className="subtitle">Vyberte svou roli pro přístup k systému</p>

        <div className="role-buttons">
          <button
            className={`role-button ${!canBeZadatel && !isITAdmin ? 'disabled' : ''}`}
            onClick={() => handleRoleClick('zadatel')}
            disabled={!canBeZadatel && !isITAdmin}
            title={!canBeZadatel && !isITAdmin ? 'Nemáte oprávnění jako žadatel' : ''}
          >
            <h2>Žadatel</h2>
            {!canBeZadatel && !isITAdmin && <span className="disabled-badge">Nedostupné</span>}
          </button>

          <button
            className={`role-button ${!canBeVedouci && !isITAdmin ? 'disabled' : ''}`}
            onClick={() => handleRoleClick('vedouci')}
            disabled={!canBeVedouci && !isITAdmin}
            title={!canBeVedouci && !isITAdmin ? 'Nemáte oprávnění jako vedoucí' : ''}
          >
            <h2>Vedoucí</h2>
            {!canBeVedouci && !isITAdmin && <span className="disabled-badge">Nedostupné</span>}
          </button>

          <button
            className={`role-button ${!canBeFinancist && !isITAdmin ? 'disabled' : ''}`}
            onClick={() => handleRoleClick('financist')}
            disabled={!canBeFinancist && !isITAdmin}
            title={!canBeFinancist && !isITAdmin ? 'Nemáte oprávnění jako hlavní ekonom' : ''}
          >
            <h2>Hlavní ekonom</h2>
            {!canBeFinancist && !isITAdmin && <span className="disabled-badge">Nedostupné</span>}
          </button>

          <button
            className={`role-button ${!canBeDirector && !isITAdmin ? 'disabled' : ''}`}
            onClick={() => handleRoleClick('director')}
            disabled={!canBeDirector && !isITAdmin}
            title={!canBeDirector && !isITAdmin ? 'Nemáte oprávnění jako ředitel' : ''}
          >
            <h2>Ředitel</h2>
            {!canBeDirector && !isITAdmin && <span className="disabled-badge">Nedostupné</span>}
          </button>

          <button
            className={`role-button ${!canBeReferent && !isITAdmin ? 'disabled' : ''}`}
            onClick={() => handleRoleClick('referent')}
            disabled={!canBeReferent && !isITAdmin}
            title={!canBeReferent && !isITAdmin ? 'Nemáte oprávnění jako obchodní referent' : ''}
          >
            <h2>Obchodní referent</h2>
            {!canBeReferent && !isITAdmin && <span className="disabled-badge">Nedostupné</span>}
          </button>
        </div>

        {currentUser && (
          <div
            style={{
              marginTop: '2rem',
              padding: '1rem 1.5rem',
              background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
              borderRadius: '12px',
              color: 'white',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
          >
            <p style={{ margin: 0, fontSize: '1rem', fontWeight: '500' }}>
              <strong>{currentUser.name}</strong>
              {currentUser.admin_type && (
                <span style={{ marginLeft: '0.5rem', opacity: 0.9 }}>
                  • {currentUser.admin_type === 'spravce_it' ? 'Správce IT' : 'Správce Budov'}
                </span>
              )}
              {!currentUser.admin_type && currentUser.role !== 'admin' && (
                <span style={{ marginLeft: '0.5rem', opacity: 0.9 }}>
                  • {getRoleLabel(currentUser.role)}
                </span>
              )}
              {isITAdmin && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.9rem', opacity: 0.95 }}>
                  ✅ Přístup ke všem rolím
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default PurchaseRoleSelector
